import { B2bSavingPlanCategory, B2bSavingPlanType } from '@Common/types/b2b/plans';
import { B2bCashSource } from '@Common/types/bo/b2b/b2b';
import {
  B2bCompanyPlanTypeParameters,
  B2bManagedPlanRebalancingFrequency,
  B2bMatchingContributionComputedBy,
  B2bPlanEntryFeePaidBy,
  B2bPlanFeePaidBy,
} from '@Common/types/bo/b2b/plans';

export const b2bCompaniesSavingPlansTranslations = {
  custom_cash_sources: {
    title: 'Sources de versement par instrument uniquement pour la Gestion Libre',
    subtitle:
      "Le fonds d'allocation par défaut du produit inclut automatiquement toutes les sources de liquidités et ne peut être modifié.",
    instrument: 'Instrument',
    isin: 'ISIN',
    matching_contribution: 'Abondement',
    all: 'Tous',
  },
  cash_sources: {
    voluntary_deposit: 'Versement volontaire',
    participation: 'Participation',
    profit_sharing: 'Intéressement',
    value_sharing_bonus: 'Prime de partage de la valeur',
    unused_vacation_days: 'Compte épargne-temps',
    ccb_matured_election: 'Échéance légale',
  } satisfies Record<B2bCashSource, string>,
  button: "Créer un plan d'épargne",
  empty: "Cette entreprise n'a pas encore de plan d'épargne",
  actions: {
    create: {
      title: "Créer un plan d'épargne",
      success: "Plan d'épargne créé",
      failure: "Échec lors de la création du Plan d'épargne de l'entreprise: %{error}",
      steps: {
        '1': 'Information générale',
        '2': 'CCB',
        '3': 'Abondements',
        '4': 'Frais',
        '5': 'Sources de versement personnalisées',
      },
    },
    edit: {
      title: "Editer un plan d'épargne",
      success: "Plan d'épargne édité",
      failure: "Échec lors de la l'édition du Plan d'épargne de l'entreprise: %{error}",
      steps: {
        '1': 'Information générale',
        '2': 'Abondements',
        '3': 'Frais',
        '4': "Date d'entrée en vigueur",
      },
    },
    add_instrument: 'Ajouter un instrument',
  },
  sections: {
    general: {
      details: 'Détails',
      default_instrument: 'Fonds par défaut',
      instruments_universe: "Univers d'investissement",
      default_profile: 'Profil par défault',
      no_profile_available: "Il n'existe pas de profil d'investissement pour cette entreprise",
      custom_profile: 'Autres profils',
      custom_profile_add: 'Ajouter un profil',
      custom_profile_placeholder: 'Selectionner un profile',
      add_mutual_fund: 'Ajouter un fonds',
      minimum_tenure: 'Ancienneté minimale en mois',
      minimum_tenure_reference_date: "Date de référence pour l'ancienneté",
      minimum_tenure_reference_date_help:
        "Si défini, l'ancienneté est vérifiée à cette date de l'année précédant l'investissement",
      plan_label_help:
        'Optionnel. Permet de distinguer deux plans de même type, qui portent sinon le même nom.',
      retirement_age_help:
        "Optionnel. Prioritaire sur l'âge de la retraite de l'entreprise. Appliqué lors du premier investissement dans un PERECO d'un salarié qui n'a pas défini lui-même son âge de départ à la retraite, ce qui le confirme.",
      name: 'Nom',
      cash_sources: "Origines d'avoirs permises",
      is_cash_sources_per_instrument: 'Personnalisation',
      is_cash_sources_per_instrument_yes: 'Paramètres personnalisés par fonds',
      is_cash_sources_per_instrument_no: 'Aucune personnalisation (appliquer à tous les fonds)',
      is_cash_sources_per_instrument_discard_warning:
        'Les paramètres par fonds seront supprimés, le plan appliquera les mêmes paramètres à tous ses fonds.',
      is_cash_sources_per_instrument_switch_warning:
        'Les choix déjà effectués ne sont pas impactés ; tout ce qui est soumis après ce changement, y compris dans les campagnes en cours, utilise les nouveaux paramètres.',
      matching_contributions: 'Abondements',
      matching_contributions_warning:
        "Veuillez d'abord activer l'abondement sur le contrat avant de vouloir l'activer sur le produit.",
      active: 'Active',
      fee_settings: 'Paramètres de fréquence',
      matching_contribution_product_limit: 'Plafond du dispositif :',
      unilateral_matching_contribution: 'Abondement unilatéral',
      unilateral_matching_contribution_limit: "Plafond annuel de l'abondement unilatéral",
      unilateral_matching_contribution_limit_help:
        'Montant maximum par salarié et par année civile, 6 000 € maximum',
      has_ccb: "Le plan d'épargne contient-il un CCB ?",
      ccb_instruments_universe: "Univers d'instruments CCB",
      ccb_parameters: 'Paramètres CCB',
      ccb_option_at_maturity: "Option à l'échéance",
      ccb_payer_of_liquidity: 'Payeur de la liquidité',
      ccb_handover_date: 'Date de remise',
      add_ccb_instrument: 'Ajouter un instrument CCB',
      ccb_option_at_maturity_consultation: 'Consultation',
      ccb_option_at_maturity_final_withdrawal: 'Retrait final',
      ccb_option_at_maturity_final_arbitrage: 'Arbitrage final',
      ccb_is_arbitrage_allowed_into_per:
        "L'arbitrage est-il autorisé dans le cadre du PER (PERCO/PERECO/PERO/PERU) ?",
      ccb_payer_of_liquidity_company: 'Entreprise',
      ccb_payer_of_liquidity_account_keeper: 'Teneur de compte',
    },
    matching_contributions: {
      general_settings: 'Paramètres généraux',
      configuration_rule: "Règle d'abondement",
      matching_contribution_pool_type: 'Type de pool d’abondement',
      configuration_rule_simple: 'Abondement simple',
      configuration_csp_code: 'Abondement par code CSP',
      configuration_csp_code_per_cash_source: 'Abondement par code CSP par source de versement',
      configuration_csp_code_per_cash_source_brackets:
        'Abondement par code CSP par source de versement par palier',
      configuration_rule_limit_brackets: 'Abondement par palier',
      configuration_rule_limit_brackets_per_cash_source:
        'Abondement par palier par source de versement',
      configuration_rule_seniority: 'Abondement par ancienneté',
      configuration_rule_company_simple: "Abondement simple calculé par l'entreprise",
      pool_type_cumulative_contributions: 'Contributions cumulées',
      pool_type_per_contribution: 'Par contribution',
      rule_settings: 'Paramétrage de la règle',
      rule_settings_per_cash_source: 'Paramétrage de la règle pour %{cash_source}',
      effective_date: "Date d'entrée en vigueur du changement",
      effective_date_now: "Date d'entrée en vigueur des changements d'abondement",
      effective_date_next: 'Appliquer sur 01/01/%{year}',
      simple: {
        source: "Origines d'avoirs abondables",
        rate: 'Taux (%)',
        limit: 'Limite €',
        limit_pct: 'Limite % PASS',
      },
      csp_code: {
        add_row: 'Ajouter un code',
        error_duplicated: 'Ce code CSP existe déjà sur ce produit.',
        no_code: 'Pas de code',
        value: 'Code CSP',
      },
      limit_brackets: {
        rate: 'Taux (%)',
        from: 'De',
        to: 'À',
        max: 'Max',
        add_row: 'Ajouter une ligne',
        at_least_one: 'Au moins une source de versement doit être activée',
      },
      seniority_brackets: {
        tenure: "Durée d'occupation en années",
        rate: 'Taux %',
        limit: 'Limite €',
        limit_pct: 'Limite % PASS',
      },
      limit_unit: 'Unité limite',
      max_amounts: 'Montants max',
      pass_limit: 'PASS pour %{year}: ',
      pct_pass_limit: '%{percentage}% de PASS: ',
      no_display_available: "Cette règle d'abondement ne peut pas être affichées.",
      no_save_available: "Cette règle d'abondement ne peut pas être sauvegardées.",
      matching_contribution_effect_modal: {
        title: 'Les modifications ne s’appliqueront pas aux abondements existants',
        body: 'La mise à jour de la configuration d’abondement ne s’appliquera qu’aux nouvelles demandes. Les abondements existants en attente ne seront pas affectés rétroactivement par ces modifications.',
      },
    },
  },
  fields: {
    id: 'ID Plan',
    social_contribution: 'Plafond d’abondement légal maximum autorisé',
    supported_contribution_types: 'Sources de trésorerie autorisées',
    configuration_type: 'Type de configuration',
    matching_contribution_pool_type: 'Type d’enveloppe d’abondement',
    mc_enabled: 'Abondements activés',
    empty: "Il n'y a pas encore de plan d'épargne de créé",
    category: 'Type de plan',
    name: 'Nom',
    default_fund_instrument_id: 'Fonds par défaut',
    default_investor_profile: "Profil d'investissement par défaut",
    other_investor_profiles: "Autres profils d'investissement",
    management_fee_pct:
      "% de frais de gestion (en % des montants d'AUM évalués tous les trimestres)",
    management_fee_paid_by: 'Payé par',
    management_fee_settings: 'Paramètres des frais de gestion',
    management_fee_pct_input: '% Frais de gestion',
    management_fee_paid_by_input: 'Payé par',
    rebalancing_frequency: 'Fréquence des arbitrages automatiques',
    instruments_universe: 'Univers d’investissement',
    rate: 'Taux %',
    minimum_tenure: 'Ancienneté minimale en mois',
    minimum_tenure_reference_date: "Date de référence pour l'ancienneté (JJ/MM)",
    matching_contribution_product_limit: 'Plafond du dispositif',
    unilateral_matching_contribution_limit: 'Abondement unilatéral',
    cash_sources: "Origines d'avoirs",
    matching_contribution_computed_by: 'Montants calculés par',
    type: 'Type',
    sub_type: 'Mode de gestion',
    plan_label: 'Libellé du plan',
    retirement_age: 'Âge de la retraite par défaut',
  },
  sub_type: {
    error: 'Au moins un mode de gestion doit être sélectionné',
  },
  errors: {
    plan_label_locked:
      'Le libellé du plan de cette entreprise sert à déterminer les allocations par défaut des campagnes et ne peut pas être modifié tant que BRAV-10741 n’est pas résolu.',
  },
  creating_plan: 'Créer',
  management_fee_paid_by: {
    company: 'Entreprise',
    employee: 'Adhérent',
  } satisfies Record<B2bPlanFeePaidBy, string>,
  entry_fee_paid_by: {
    company: 'Entreprise',
    user: 'Adhérent',
  } satisfies Record<B2bPlanEntryFeePaidBy, string>,
  matching_contribution_table_title: 'Paramètres abondements',
  has_custom_matching_contribution_config:
    "Paramètres d'abondements personnalisés, veuillez contacter l'administrateur pour plus d'informations",
  custody_fees_table: {
    title: 'Paramètrage des frais de tenue de compte',
    employee_status: 'Appliquer aux adhérents dont le statut est',
    amount: 'Montant annuel (EUR)',
    paid_by: 'Payé par',
  },
  management_fees_table: {
    title: 'Paramètrage des frais de gestion',
    employee_status: 'Appliquer aux adhérents dont le statut est',
    amount: "% Frais (en % des montants d'AUM évalués tous les trimestres)",
    paid_by: 'Payé par',
  },
  entry_fees_table: {
    title: 'Paramètres des frais d’entrée',
    employee_status: 'Appliqué aux employés avec le statut',
    isin: 'ISIN',
    amount_pct: 'Total frais d’entrée %',
    instrument: 'Instrument',
    legal_status: 'Statut juridique',
    paid_by: 'Payé par',
    fcpe_paid_by: 'Les frais d’entrée du FCPE seront payés par',
    employee_pct: '% Salarié',
    employee_pct_note:
      '% Salarié est la part des frais d’entrée payée par le salarié, le reste sera payé par l’entreprise.',
    amount_pct_company: '% Entreprise',
    amount_pct_employee: '% Salarié',
    entry_fee_split_note:
      '% Entreprise et % Salarié sont tous deux des pourcentages du montant de l’ordre. Le total des frais d’entrée % est calculé automatiquement comme leur somme.',
    cash_source: 'Source de versement',
    cash_source_default: 'Par défaut',
    cash_source_note:
      'Les frais par défaut s’appliquent à toutes les sources de versement. Ajoutez une ligne pour surcharger une source spécifique.',
    add_cash_source_fee: 'Ajouter des frais pour une source de versement',
    remove_cash_source_fee: 'Retirer les frais de cette source de versement',
  },
  max_tooltips: {
    minimum_tenure: '3 mois',
    bracket_1_limit:
      'PEI: 8% du PASS ("Plafond Annuel de la Sécurité Sociale")\nPERECOI: 16% du PASS ("Plafond Annuel de la Sécurité Sociale")',
    bracket_1_contribution: '300%',
    bracket_2_limit:
      'PEI: 8% du PASS ("Plafond Annuel de la Sécurité Sociale")\nPERECOI: 16% du PASS ("Plafond Annuel de la Sécurité Sociale")',
    bracket_2_contribution: '300%',
  },
  types: {
    PEI_LIBRE: B2bCompanyPlanTypeParameters.PEI_LIBRE.name,
    PEI_PROFILE: B2bCompanyPlanTypeParameters.PEI_PROFILE.name,
    PEI: B2bCompanyPlanTypeParameters.PEI.name,
    PEE_LIBRE: B2bCompanyPlanTypeParameters.PEE_LIBRE.name,
    PEE_PROFILE: B2bCompanyPlanTypeParameters.PEE_PROFILE.name,
    PEE: B2bCompanyPlanTypeParameters.PEE.name,
    PEEG_LIBRE: B2bCompanyPlanTypeParameters.PEEG_LIBRE.name,
    PEEG_PROFILE: B2bCompanyPlanTypeParameters.PEEG_PROFILE.name,
    PEEG: B2bCompanyPlanTypeParameters.PEEG.name,
    PERECOI_LIBRE: B2bCompanyPlanTypeParameters.PERECOI_LIBRE.name,
    PERECOI_PILOTE: B2bCompanyPlanTypeParameters.PERECOI_PILOTE.name,
    PERECOI: B2bCompanyPlanTypeParameters.PERECOI.name,
    PERECO_LIBRE: B2bCompanyPlanTypeParameters.PERECO_LIBRE.name,
    PERECO_PILOTE: B2bCompanyPlanTypeParameters.PERECO_PILOTE.name,
    PERECO: B2bCompanyPlanTypeParameters.PERECO.name,
    PERECOG_LIBRE: B2bCompanyPlanTypeParameters.PERECOG_LIBRE.name,
    PERECOG_PILOTE: B2bCompanyPlanTypeParameters.PERECOG_PILOTE.name,
    PERECOG: B2bCompanyPlanTypeParameters.PERECOG.name,
    PERCOI_LIBRE: B2bCompanyPlanTypeParameters.PERCOI_LIBRE.name,
    PERCOI_PILOTE: B2bCompanyPlanTypeParameters.PERCOI_PILOTE.name,
    PERCOI: B2bCompanyPlanTypeParameters.PERCOI.name,
    PERCO_LIBRE: B2bCompanyPlanTypeParameters.PERCO_LIBRE.name,
    PERCO_PILOTE: B2bCompanyPlanTypeParameters.PERCO_PILOTE.name,
    PERCO: B2bCompanyPlanTypeParameters.PERCO.name,
    PERO_LIBRE: B2bCompanyPlanTypeParameters.PERO_LIBRE.name,
    PERO_PILOTE: B2bCompanyPlanTypeParameters.PERO_PILOTE.name,
    PERO: B2bCompanyPlanTypeParameters.PERO.name,
    PERU_LIBRE: B2bCompanyPlanTypeParameters.PERU_LIBRE.name,
    PERU_PILOTE: B2bCompanyPlanTypeParameters.PERU_PILOTE.name,
    PERU: B2bCompanyPlanTypeParameters.PERU.name,
    PER_LIBRE: B2bCompanyPlanTypeParameters.PER_LIBRE.name,
    PER_PILOTE: B2bCompanyPlanTypeParameters.PER_PILOTE.name,
    PER: B2bCompanyPlanTypeParameters.PER.name,
    ARTICLE_82_LIBRE: B2bCompanyPlanTypeParameters.ARTICLE_82_LIBRE.name,
    ARTICLE_82_PILOTE: B2bCompanyPlanTypeParameters.ARTICLE_82_PILOTE.name,
    ARTICLE_82: B2bCompanyPlanTypeParameters.ARTICLE_82.name,
    ARTICLE_83_LIBRE: B2bCompanyPlanTypeParameters.ARTICLE_83_LIBRE.name,
    ARTICLE_83_PILOTE: B2bCompanyPlanTypeParameters.ARTICLE_83_PILOTE.name,
    ARTICLE_83: B2bCompanyPlanTypeParameters.ARTICLE_83.name,
    PERECOER_LIBRE: B2bCompanyPlanTypeParameters.PERECOER_LIBRE.name,
    PERECOER_PILOTE: B2bCompanyPlanTypeParameters.PERECOER_PILOTE.name,
    PERECOER: B2bCompanyPlanTypeParameters.PERECOER.name,
    ER_EXTERNAL: B2bCompanyPlanTypeParameters.ER_EXTERNAL.name,
  } satisfies Record<B2bSavingPlanType | B2bSavingPlanCategory, string>,
  types_short: {
    PEI_LIBRE: 'PEI Libre',
    PEI_PROFILE: 'PEI Profilé',
    PEI: 'PEI',
    PEE_LIBRE: 'PEE Libre',
    PEE_PROFILE: 'PEE Profilé',
    PEE: 'PEE',
    PEEG_LIBRE: 'PEG Libre',
    PEEG_PROFILE: 'PEG Profilé',
    PEEG: 'PEG',
    PERECOI_LIBRE: 'PERECOI Libre',
    PERECOI_PILOTE: 'PERECOI Piloté',
    PERECOI: 'PERECOI',
    PERECO_LIBRE: 'PERECO Libre',
    PERECO_PILOTE: 'PERECO Piloté',
    PERECO: 'PERECO',
    PERECOG_LIBRE: 'PERECOG Libre',
    PERECOG_PILOTE: 'PERECOG Piloté',
    PERECOG: 'PERECOG',
    PERCOI_LIBRE: 'PERCOI Libre',
    PERCOI_PILOTE: 'PERCOI Piloté',
    PERCOI: 'PERCOI',
    PERCO_LIBRE: 'PERCO Libre',
    PERCO_PILOTE: 'PERCO Piloté',
    PERCO: 'PERCO',
    PERO_LIBRE: 'PERO Libre',
    PERO_PILOTE: 'PERO Piloté',
    PERO: 'PERO',
    PERU_LIBRE: 'PERU Libre',
    PERU_PILOTE: 'PERU Piloté',
    PERU: 'PERU',
    PER_LIBRE: 'PER Libre',
    PER_PILOTE: 'PER Piloté',
    PER: 'PER',
    ARTICLE_82_LIBRE: 'Article 82 Libre',
    ARTICLE_82_PILOTE: 'Article 82 Piloté',
    ARTICLE_82: 'Article 82',
    ARTICLE_83_LIBRE: 'Article 83 Libre',
    ARTICLE_83_PILOTE: 'Article 83 Piloté',
    ARTICLE_83: 'Article 83',
    PERECOER_LIBRE: 'PERECO ER Libre',
    PERECOER_PILOTE: 'PERECO ER Piloté',
    PERECOER: 'PERECO ER',
    ER_EXTERNAL: 'ER Externe',
  } satisfies Record<B2bSavingPlanType | B2bSavingPlanCategory, string>,
  rebalancing_frequency: {
    values: {
      '1_month': 'Tous les mois',
      '2_months': 'Tous les 2 mois',
      '3_months': 'Tous les 3 mois',
      '4_months': 'Tous les 4 mois',
      '6_months': 'Tous les 6 mois',
      '12_months': 'Tous les 12 mois',
    } satisfies Record<B2bManagedPlanRebalancingFrequency, string>,
  },
  matching_contribution_computed_by: {
    distributor: 'Teneur de compte',
    company: 'Entreprise',
  } satisfies Record<B2bMatchingContributionComputedBy, string>,
};
